import { useState, useMemo, MouseEvent, ChangeEvent, Dispatch, SetStateAction, useEffect, useRef } from "react";
import { DateTime } from "luxon";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableSortLabel from "@mui/material/TableSortLabel";
import Box from "@mui/material/Box";
import { visuallyHidden } from "@mui/utils";
import Toolbar from "@mui/material/Toolbar";
import { Button, TextField } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import Paper from "@mui/material/Paper";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import Checkbox from "@mui/material/Checkbox";
import TablePagination from "@mui/material/TablePagination";
import { EnvironmentState, IEnvironment } from "../../../types/EnvironmentTypes";
import { getEnvironmentStageData } from "../../../services/registry/EnvironmentService";
import { getComparator, Order, stableSort } from "../../../utils/Table";
import { EnvironmentStateIndicator } from "./EnvironmentStateIndicator";
import Typography from "@mui/material/Typography";
import { IBackendImage } from "../../../services/registry/BackendImageService";
import * as React from "react";

const STORAGE_ENV_PAGE_CONFIG_KEY = "envPageConfig";
const STORAGE_ENV_FILTER_KEY = "envFilterQuery";
interface HeadCell {
  id: keyof IEnvironment;
  label: string;
  align?: "inherit" | "left" | "center" | "right" | "justify";
  padding?: "normal" | "checkbox" | "none";
}

const headCells: readonly HeadCell[] = [
  {
    id: "name",
    label: "Name",
    padding: "normal",
  },
  {
    id: "stage",
    label: "Environment Type",
  },
  {
    id: "baseURL",
    label: "Base URL",
  },
  {
    id: "backendImageName",
    label: "Image Name",
  },
  {
    id: "deploymentDate",
    label: "Deployment Date",
    align: "right",
  },
  {
    id: "creationDate",
    label: "Creation Date",
    align: "right",
  },
  {
    id: "state",
    label: "State",
    align: "right",
  },
];

interface EnvironmentsTableHeadProps {
  onRequestSort: (event: MouseEvent<unknown>, property: keyof IEnvironment) => void;
  order: Order;
  orderBy: string;
}

const EnvironmentsTableHead = ({ order, orderBy, onRequestSort }: EnvironmentsTableHeadProps) => {
  const createSortHandler = (property: keyof IEnvironment) => (event: MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.align}
            padding={headCell.padding}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

export interface EnvironmentsTableProps {
  rows: IEnvironment[];
  backendImages: IBackendImage[];
  selectedItem: IEnvironment | undefined;
  setSelectedItem: Dispatch<SetStateAction<IEnvironment | undefined>>;
  onNewButtonClick: () => void;
  onRefreshButtonClick: () => void;
  onPageChange: (page: number, rowsPerPage: number, visibleRows: IEnvironment[]) => void;
  initialFilterQuery: string | undefined;
}

export const EnvironmentsTable = ({
  rows,
  backendImages,
  selectedItem,
  setSelectedItem,
  onNewButtonClick,
  onRefreshButtonClick,
  onPageChange,
  initialFilterQuery,
}: EnvironmentsTableProps) => {
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<keyof IEnvironment>("name");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [includeTerminated, setIncludeTerminated] = useState(false);
  const [filterQuery, setFilterQuery] = useState<string>("");
  const filterInitRef = useRef(false);
  const pageConfig = localStorage.getItem(STORAGE_ENV_PAGE_CONFIG_KEY);

  useEffect(() => {
    if (!filterInitRef.current) {
      const savedFilter = localStorage.getItem(STORAGE_ENV_FILTER_KEY);
      const defaultFilter = "dev1";
      if (initialFilterQuery) {
        setFilterQuery(initialFilterQuery);
        localStorage.setItem(STORAGE_ENV_FILTER_KEY, initialFilterQuery);
      } else if (savedFilter !== null) {
        setFilterQuery(savedFilter);
      } else {
        setFilterQuery(defaultFilter);
        localStorage.setItem(STORAGE_ENV_FILTER_KEY, defaultFilter);
      }
      filterInitRef.current = true;
    }
  }, [initialFilterQuery]);

  useEffect(() => {
    setRowsPerPage(parseInt(pageConfig ?? "10", 10));
  }, [pageConfig]);

  const requestSortHandler = (event: MouseEvent<unknown>, property: keyof IEnvironment) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleFilterChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newFilter = event.target.value;
    setFilterQuery(newFilter);
    localStorage.setItem(STORAGE_ENV_FILTER_KEY, newFilter);
  };

  const handleRowClick = (row: IEnvironment) => {
    setSelectedItem(selectedItem?.name === row.name ? undefined : row);
  };

  const pageChangeHandler = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const rowsPerPageChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    localStorage.setItem(STORAGE_ENV_PAGE_CONFIG_KEY, event.target.value);
    setPage(0);
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const filteredRows = useMemo(() => {
    return rows.filter(
      (t) =>
        (includeTerminated || t.terminationDate === undefined) &&
        (t.name.toLowerCase().includes(filterQuery.toLowerCase()) ||
          t.backendImageName?.split(":")[1].toLowerCase().includes(filterQuery.toLowerCase())),
    );
  }, [rows, includeTerminated, filterQuery]);

  const visibleRows = useMemo(() => {
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    return stableSort(filteredRows as any, getComparator(order, orderBy)).slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );
  }, [filteredRows, order, orderBy, page, rowsPerPage]);

  useEffect(() => {
    onPageChange(page, rowsPerPage, visibleRows);
  }, [onPageChange, page, rowsPerPage, visibleRows]);

  const isSelected = (row: IEnvironment, selectedItem: IEnvironment | undefined) =>
    selectedItem !== undefined && row.name === selectedItem.name;

  const getBackendImageName = (backendImageId: string, backendImages: IBackendImage[]) => {
    const backendImage = backendImages.find((t) => t.id === backendImageId);
    if (!backendImage) {
      return "UNKNOWN";
    }
    const latestBackendImageFromBranch = backendImages
      .filter((t) => t.branch === backendImage.branch)
      .sort((a, b) => b.creationDate - a.creationDate)[0];
    const isLatest = backendImage.id === latestBackendImageFromBranch.id;
    if (isLatest) {
      return (
        <>
          {backendImage.branch}
          {" ("}
          <b style={{ color: "green" }}>latest</b>
          {")"}
        </>
      );
    } else {
      return (
        <>
          {backendImage.branch}
          {" ("}
          <b style={{ color: "red" }}>outdated</b>
          {")"}
        </>
      );
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", mb: 2 }}>
        <Toolbar sx={{ pl: { sm: 2 }, pr: { xs: 1, sm: 1 } }}>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={onNewButtonClick}>
            New
          </Button>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={onRefreshButtonClick} style={{ marginLeft: 8 }}>
            Refresh
          </Button>
          <TextField
            id="outlined-search"
            label="Search by name/image name"
            type="search"
            size="small"
            onChange={handleFilterChange}
            sx={{ width: "360px", marginLeft: 1 }}
            value={filterQuery}
          />
        </Toolbar>
        <TableContainer>
          <Table sx={{ minWidth: 750 }} size="small">
            <colgroup>
              <col style={{ width: "15%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "20%" }} />
              <col style={{ width: "20%" }} />
              <col style={{ width: "13%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "10%" }} />
            </colgroup>
            <EnvironmentsTableHead order={order} orderBy={orderBy} onRequestSort={requestSortHandler} />
            <TableBody>
              {visibleRows.map((row, index) => {
                const labelId = `environments-table-row-${index}`;
                const creationDate = DateTime.fromSeconds(row.creationDate).toLocaleString(
                  DateTime.DATETIME_SHORT_WITH_SECONDS,
                );
                const deployDate = row.deploymentDate
                  ? DateTime.fromSeconds(row.deploymentDate).toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS)
                  : "";
                return (
                  <TableRow
                    hover
                    onClick={() => handleRowClick(row)}
                    role="checkbox"
                    tabIndex={-1}
                    key={row.name}
                    selected={isSelected(row, selectedItem)}
                    sx={{
                      cursor: "pointer",
                      "&.Mui-selected": {
                        backgroundColor: (theme) =>
                          theme.palette.mode === "light" ? "rgba(25, 118, 210, 0.12)" : "rgba(255, 255, 255, 0.16)",
                      },
                      "&.Mui-selected:hover": {
                        backgroundColor: (theme) =>
                          theme.palette.mode === "light" ? "rgba(25, 118, 210, 0.18)" : "rgba(255, 255, 255, 0.24)",
                      },
                    }}
                  >
                    <TableCell component="th" id={labelId} scope="row" padding="none" style={{ paddingLeft: 16 }}>
                      {row.name + (row.isDefault ? " (default)" : "")}
                    </TableCell>
                    <TableCell>
                      {getEnvironmentStageData(row.stage)[2]} (Stage: {row.stage})
                    </TableCell>
                    <TableCell align="left">{row.baseURL}</TableCell>
                    <TableCell align="left">{getBackendImageName(row.backendImageId, backendImages)}</TableCell>
                    <TableCell align="right">{deployDate}</TableCell>
                    <TableCell align="right">{creationDate}</TableCell>
                    <TableCell align="right">
                      <EnvironmentStateIndicator state={row.state || EnvironmentState.UNKNOWN} showLabel={true} />
                    </TableCell>
                  </TableRow>
                );
              })}
              {emptyRows > 0 && (
                <TableRow style={{ height: 33 * emptyRows }}>
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <div
          style={{ display: "flex", flex: 1, flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}
        >
          <div style={{ display: "flex", flex: 1, flexDirection: "row", alignItems: "center" }}>
            <Checkbox
              color="primary"
              checked={includeTerminated}
              onChange={() => setIncludeTerminated(!includeTerminated)}
            />
            <Typography variant="body2">Show terminated environments</Typography>
          </div>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={filteredRows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={pageChangeHandler}
            onRowsPerPageChange={rowsPerPageChangeHandler}
          />
        </div>
      </Paper>
    </Box>
  );
};
